import { useState } from 'react';
import { CardHeader, EmptyState, Panel, SegmentedControl, TrendChart, type TrendChartVariant } from '@bcecloud/portal-ui';
import { CHART_VARIANTS, INTERVAL_LABELS } from '@/lib/report-window';
import { TogglePill } from '@/components/report-bits';
import {
  CHART_MODE,
  NO_SPLIT,
  ROLLING_LABEL,
  ROLLING_MONTHS,
  SALES_GROUP_LABEL,
  SALES_METRIC_SPEC,
  salesChart,
  type ChartMode,
  type RollingMonths,
  type SalesAnalytics,
  type SalesGroup,
  type SalesMetric,
  type SalesSplit,
} from '@/lib/sales-analytics';

export interface SalesTrendPanelProps {
  readonly data: SalesAnalytics;
  readonly metric: SalesMetric;
  readonly onMetricChange: (metric: SalesMetric) => void;
  readonly split: SalesSplit;
  readonly onSplitChange: (split: SalesSplit) => void;
}

/** Bars, a line, or the trend lines alone (the series off, so the axis fits the trends). */
const TRENDS_ONLY = 'trends';
type ChartLook = TrendChartVariant | typeof TRENDS_ONLY;
const CHART_LOOKS: readonly { key: ChartLook; label: string }[] = [...CHART_VARIANTS, { key: TRENDS_ONLY, label: 'Trends only' }];
/** Bars first, the shared analytics default. */
const DEFAULT_LOOK: ChartLook = CHART_VARIANTS[0]?.key ?? TRENDS_ONLY;

const CHART_MODES: readonly { key: ChartMode; label: string }[] = [
  { key: CHART_MODE.periodic, label: 'Per period' },
  { key: CHART_MODE.cumulative, label: 'Cumulative' },
];

/** The 12-month trend and last year on by default: the page's question is whether we are up on last year. */
const DEFAULT_ROLLING: ReadonlySet<RollingMonths> = new Set([12]);

/**
 * The trend chart: one metric per bucket or as a running total, as bars, a
 * line or the trend lines alone, optionally split, with trailing 3/6/12-month trend lines (sums
 * rescaled to the bucket, so they sit on the bars' axis at every grain) and
 * the same period last year.
 */
export function SalesTrendPanel({ data, metric, onMetricChange, split, onSplitChange }: SalesTrendPanelProps): React.ReactElement {
  const [look, setLook] = useState<ChartLook>(DEFAULT_LOOK);
  const [mode, setMode] = useState<ChartMode>(CHART_MODE.periodic);
  const [rolling, setRolling] = useState<ReadonlySet<RollingMonths>>(DEFAULT_ROLLING);
  const [lastYear, setLastYear] = useState(true);

  const spec = SALES_METRIC_SPEC[metric];
  // Ratios have no running total; the toggle stays put and the chart reads per period.
  const cumulative = mode === CHART_MODE.cumulative && !spec.ratio;
  // A running total is the line itself, so "trends only" has nothing to hide there.
  const trendsOnly = look === TRENDS_ONLY && !cumulative;
  // Trends only with no window picked would be an empty chart: the 12-month trend stands in.
  const shownRolling = trendsOnly && rolling.size === 0 ? DEFAULT_ROLLING : rolling;
  const chart = salesChart(data, metric, { mode, rolling: shownRolling, lastYear, trendsOnly });
  const variant: TrendChartVariant = look === TRENDS_ONLY || (spec.ratio && chart.series) ? 'line' : look;
  const toggleRolling = (months: RollingMonths): void =>
    setRolling((previous) => {
      const next = new Set(previous);
      if (next.has(months)) next.delete(months);
      else next.add(months);
      return next;
    });

  return (
    <Panel className="p-5">
      <CardHeader
        title={`${spec.label}${cumulative ? ', running total' : ' over time'}`}
        description={`${INTERVAL_LABELS[data.range.interval]} · won by signed date, lost by the day it closed, created by the day it was opened. Trend lines average the trailing months to each bar's length${chart.series ? '; they and last year are the whole team' : ''}.`}
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SegmentedControl<SalesMetric>
          label="Show"
          value={metric}
          onChange={onMetricChange}
          options={(Object.keys(SALES_METRIC_SPEC) as SalesMetric[]).map((key) => ({ key, label: SALES_METRIC_SPEC[key].label }))}
        />
        <SegmentedControl<ChartMode> label="Mode" value={mode} onChange={setMode} options={CHART_MODES.map((option) => ({ ...option }))} />
        <SegmentedControl<ChartLook>
          label="Chart type"
          value={look}
          onChange={setLook}
          options={CHART_LOOKS.map((option) => ({ key: option.key, label: option.label }))}
        />
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SegmentedControl<SalesSplit>
          label="Split by"
          value={split}
          onChange={onSplitChange}
          options={[
            { key: NO_SPLIT, label: 'None' },
            ...(Object.keys(SALES_GROUP_LABEL) as SalesGroup[]).map((key) => ({ key, label: SALES_GROUP_LABEL[key] })),
          ]}
        />
        {cumulative
          ? null
          : ROLLING_MONTHS.map((months) => (
              <TogglePill key={months} on={shownRolling.has(months)} onToggle={() => toggleRolling(months)}>
                {ROLLING_LABEL[months]}
              </TogglePill>
            ))}
        <TogglePill on={lastYear} onToggle={() => setLastYear((previous) => !previous)}>
          Last year
        </TogglePill>
      </div>
      {chart.points.every((point) => point.value === 0) && chart.overlays.every((overlay) => overlay.values.every((value) => !value)) ? (
        <EmptyState title="Nothing in this range" message="Try a longer range or another metric." />
      ) : (
        <TrendChart
          points={chart.points}
          series={chart.series}
          overlays={chart.overlays}
          // Ratios cannot stack: a split win rate or average is one line per segment.
          variant={variant}
          hideSeries={trendsOnly}
          title={spec.label}
          unit={spec.unit}
          formatValue={spec.format}
        />
      )}
    </Panel>
  );
}
